<!--
  The one piece of chrome. Deliberately thin: this is a reading surface, and a
  toolbar that competes with the document is the fastest way to stop it being
  one.

  The structure is unomena.com's header, verbatim: a `.wordmark` hard against
  the left edge of the shell container, a `.desktop-nav` list of `.meta-label
  .link-grow` items hard against the right, and nothing in the middle fighting
  either of them. There is no icon -- unomena.com's header has none, and the
  wordmark IS the mark: lowercase letters plus a red full stop.

  The document context (back arrow, document name) hangs off the LEFT group,
  after the wordmark rather than before it, so the wordmark starts at the same
  x on every route. Putting the arrow first shifted the wordmark right by the
  width of the arrow on document pages only, which is exactly the kind of
  one-page-only drift the single `--shell-width` container exists to prevent.
-->
<script lang="ts">
  import { untrack } from 'svelte'
  import { navigate } from '../router.svelte'
  import { signOut } from '../api'
  import type { Snippet } from 'svelte'

  /*
   * The scroll collapse, lifted from unomena.com's Header.svelte: `unomena`
   * folds to `uno` once the page is underway. Ours folds the SAME four glyphs
   * with the same stagger, so it is the same animation rather than a
   * lookalike; `docs` sits outside the folding group, which is what makes the
   * two resting states `unomena docs.` and `uno docs.`
   *
   * There are TWO speeds, and which one applies is the page's business.
   *
   *   * 8/4 on the sign-in page: the mark folds as soon as the page moves.
   *     The traveling dot cannot leave the bar while the wordmark is whole
   *     (`retarget` rule 1), and on THIS page the headline's full stop crosses
   *     the middle of the viewport after 13 px of scroll -- measured. The
   *     site's own 96 would free the dot long after the headline had gone by,
   *     so the headline would never be a station, which is the fault Euan
   *     reported. Their 96 is right for their hero, which is half a screen
   *     taller; the behaviour is what is being matched, not the number.
   *   * 480/240 inside the app. This bar sits above something being READ, and
   *     a logo moving in the corner of the eye mid-paragraph is a small
   *     distraction repeated all day. It keeps the same 2:1 asymmetry, which
   *     is what stops the mark flapping when the scroll sits on the trigger,
   *     but puts the trigger far enough down that it never fires on the
   *     document list or on the first screen of a document.
   *
   * Both are here rather than one being "the" threshold, because the reason
   * they differ is the page, not a preference.
   */
  const SITE_FOLD = { fold: 8, unfold: 4 }
  const APP_FOLD = { fold: 480, unfold: 240 }
  const FOLD = [...'mena']
  /*
   * unomena.com's own nav, ON THE SIGN-IN PAGE ONLY.
   *
   * It used to be on every page, so the two halves read as one place. Euan
   * reversed that on 2026-09-21, having lived with it: "when you're signed in
   * and working on a document, you should not see the website's menu items at
   * the top... It is correct to have the MCP link and the sign-out link at the
   * top and no website footer at the bottom."
   *
   * Which is the same rule the footer already follows. Signed out, this is a
   * page of unomena.com and it carries the site's chrome; signed in, it is a
   * tool with a document open in it, and six links to marketing pages are six
   * ways out of what you are doing. The continuity is still there in the
   * direction that pays: the site's own header carries "Docs" at its end.
   *
   * Copied from ../unomena-website/src/lib/data/site.ts (`nav`), with the
   * site's own "See the work" label for the portfolio, and absolute because
   * they leave this app. Hidden below 64rem, where even the sign-in page has
   * better uses for the width -- the site answers that width with a Menu
   * dialog, which is more chrome than this bar wants.
   */
  const SITE_NAV = [
    { label: 'See the work', href: 'https://unomena.com/portfolio' },
    { label: 'Services', href: 'https://unomena.com/services' },
    { label: 'How we build', href: 'https://unomena.com/how-we-build' },
    { label: 'About', href: 'https://unomena.com/about' },
    { label: 'Engagements', href: 'https://unomena.com/engagements' },
    { label: 'Contact', href: 'https://unomena.com/contact' },
  ]

  let y = $state(0)
  let signingOut = $state(false)

  async function onSignOut() {
    signingOut = true
    await signOut()
  }
  let folded = $state(false)

  /*
   * Under `prefers-reduced-motion: reduce` the mark does not fold AT ALL,
   * rather than folding instantly.
   *
   * An instant fold would still be the thing that setting exists to suppress:
   * unrequested movement in the periphery, triggered by scrolling. There is
   * nothing to reveal by collapsing it -- the bar has room either way -- so
   * the honest response to "less motion" is to leave the wordmark whole.
   */
  let mayFold = $state(true)

  $effect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const sync = () => {
      mayFold = !mq.matches
    }
    sync()
    mq.addEventListener('change', sync)
    return () => mq.removeEventListener('change', sync)
  })

  $effect(() => {
    folded = mayFold && y > (untrack(() => folded) ? foldAt.unfold : foldAt.fold)
  })

  let {
    back,
    docName,
    status,
    live = false,
    bare = false,
    guest = false,
    presence,
    docActions,
    children,
  }: {
    back?: string
    docName?: string
    status?: string
    live?: boolean
    /**
     * A stranger reading a published document (TASK-049). They have no
     * account, so "Sign out" is a lie and "Back to all documents" is a door
     * into somebody else's. One item instead: the way in, if they want one.
     */
    guest?: boolean
    /**
     * The sign-in screen's bar. Same wordmark, same rule; the one item on the
     * right is "Back" to unomena.com, because that is the only place to go
     * from a page you have not signed in to yet. Everywhere else keeps Sign
     * out, which is the door out of the app and belongs nowhere else.
     */
    bare?: boolean
    /** The viewers strip (TASK-041), on a document page. Hidden where the bar is too narrow for it. */
    presence?: Snippet
    /**
     * Actions belonging to the DOCUMENT rather than to the app: Share, today.
     *
     * A slot of its own, next to the viewers, rather than passed as
     * `children` into the nav. The nav is where the app's own items live --
     * MCP, Sign out -- and a control that acts on the document one is looking
     * at belongs beside the people looking at it, not beside the way out.
     */
    docActions?: Snippet
    children?: Snippet
  } = $props()

  /** The sign-in page folds at the site's speed; the app at its own. */
  const foldAt = $derived(bare ? SITE_FOLD : APP_FOLD)

</script>

<svelte:window bind:scrollY={y} />

<header class="topbar">
  <div class="topbar-inner">
    <div class="bar-left">
      <!--
        The letters are aria-hidden and the link is named by its label, so the
        fold -- which is decoration -- cannot change what a screen reader is
        told the link is. Whitespace between these spans would become nothing
        (whitespace-only anonymous flex items are not generated), which is why
        the space before `docs` is a non-breaking one INSIDE its span.
      -->
      <a
        class="wordmark"
        class:folded
        href="/"
        aria-label="Unomena Docs — home"
        onclick={(e) => navigate(e, '/')}
      >
        <span class="letters" aria-hidden="true"
          ><span class="ch">uno</span>{#each FOLD as c, i (i)}<span class="fold" style="--i: {i}"
              ><span class="fold-inner">{c}</span></span
            >{/each}<span class="ch">&nbsp;docs</span><span class="dot">.</span></span
        >
      </a>

      {#if back}
        <span class="bar-rule" aria-hidden="true"></span>
        <a
          class="back-link"
          href={back}
          aria-label="Back to all documents"
          onclick={(e) => navigate(e, back)}>&larr;</a
        >
      {/if}

      {#if docName}
        <!--
          Not an h1: the document renders its own title heading into <main>, and
          two h1 elements on one page leave a screen-reader user with two
          competing answers to "what is this page". TASK-034.
        -->
        <div class="doc-name">{docName}</div>
      {/if}
    </div>

    <div class="bar-right">
      {#if presence}
        <div class="bar-presence">{@render presence()}</div>
      {/if}

      {#if docActions}
        <div class="bar-doc-actions">{@render docActions()}</div>
      {/if}

      {#if status}
        <!--
          The chip is the only signal that the stream dropped or a revision
          landed, so it has to be announced, politely. TASK-034.
        -->
        <span class="status-chip" class:live role="status" aria-live="polite">{status}</span>
      {/if}

      <!--
        The nav confers the meta-label + link-grow suit on whatever it is
        handed, so a route can pass a plain <a class="linkish"> as a child and
        still get an item identical to the sign-out button beside it.
      -->
      <nav class="desktop-nav" aria-label="Primary">
        <ul>
          {#if bare}
            {#each SITE_NAV as item (item.href)}
              <li class="site-link">
                <a class="linkish meta-label link-grow" href={item.href}>{item.label}</a>
              </li>
            {/each}
            <!-- A real link: it leaves the app, and the site it returns to is
                 not part of this SPA. -->
            <li><a class="linkish meta-label link-grow" href="https://unomena.com">Back</a></li>
          {:else if guest}
            <!-- An invitation, not a bounce: they came for the document and
                 they have it. A real link because signing in reloads the app
                 with a session, which the client router cannot fake. -->
            <li><a class="linkish meta-label link-grow" href="/login">Sign in</a></li>
          {:else}
            {#if children}
              <li>{@render children()}</li>
            {/if}
            <li>
              <button
                type="button"
                class="linkish meta-label link-grow"
                disabled={signingOut}
                onclick={onSignOut}>Sign out</button
              >
            </li>
          {/if}
        </ul>
      </nav>
    </div>
  </div>
</header>
